import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Skeleton,
  Space,
  Spin,
  Table,
  Tabs,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { moneyPerUnit } from "../../shared/useRateUnit";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import BillingDrillTab from "./BillingDrillTab";
import {
  ExecutionDetailTab,
  ExecutionTaskRow,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtMonthLong,
  fmtQty,
  num,
} from "./executionShared";

// Month drill-down for the Monthly Execution Summary — a SEPARATE SCREEN (not
// a drawer) because one month can touch 900+ tasks. Labels follow the top
// sheet's vocabulary: "Target" = executed at planned rates (the earned*
// fields), "Actual" = consumed cost, "Actual Client Billing" = qty × Basic
// Rate. Tasks tab = activity rollup expanding into server-paginated task rows
// with scheduled/actual/carry quantities; item tabs show planned vs target
// qty+cost per material/labour/machine with a Deviation (vs Planned) column;
// Billing tab = the running-meter drill.

const PAGE_SIZE = 50;

const statChip = (label: string, value: number, color: string, signed = false) => (
  <Tooltip title={signed ? fmtINRSigned(value, true) : fmtINR(value)} key={label}>
    <div
      style={{
        background: "#f7f9fc",
        border: "1px solid #e3e9f3",
        borderRadius: 8,
        padding: "6px 14px",
        minWidth: 130,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#8094ae" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
        {signed ? fmtINRSigned(value) : fmtINRCompact(value)}
      </div>
    </div>
  </Tooltip>
);

const costCell = (value: string | number) => (
  <Tooltip title={fmtINR(value)}>
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
  </Tooltip>
);

const boldCostCell = (value: string | number) => (
  <Tooltip title={fmtINR(value)}>
    <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
      {fmtINRCompact(value)}
    </b>
  </Tooltip>
);

// Deviation convention (matches PlannedVsActualTree): quiet dash while nothing
// is earned (the Earned column's "pending" pill already says why); red when
// over the plan, green when under.
const deviationCell = (value: string | number, earned: string | number) => {
  if (num(earned) === 0) {
    return (
      <Tooltip title="No executed value yet for this row">
        <span style={{ color: "#c9d2e3" }}>—</span>
      </Tooltip>
    );
  }
  const deviation = num(value);
  return (
    <Tooltip title={fmtINRSigned(deviation, true)}>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          color: deviation === 0 ? "#8c94a8" : deviation > 0 ? "#cf1322" : "#237804",
        }}
      >
        {fmtINRSigned(deviation)}
      </span>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Quiet breakdown grammar — ONE table, ONE column grid for every level, so the
// ₹ columns line up straight down the page. Hierarchy is carried by
// indentation, weight and whitespace; the only color inside the grid is the
// deviation sign (red over plan / green under). Levels: 0 head group,
// 1 item (norm / tool / labour / material), 2 engine cost head, 3 machine.
// ---------------------------------------------------------------------------

// Earned ₹: real money in ink, one muted word until actuals land.
const earnedCell = (value: string | number) =>
  num(value) === 0 ? (
    <Tooltip title="No actuals captured yet">
      <span style={{ color: "#a9b6c9" }}>pending</span>
    </Tooltip>
  ) : (
    boldCostCell(value)
  );

// Equipment cost-head display order + labels — mirrors the task card's
// Equipment Hourly Use Rate breakdown (TaskQuickViewCard EQUIPMENT_HEAD_ORDER).
const EQUIPMENT_HEAD_ORDER: Array<[string, string]> = [
  ["depreciation", "Depreciation"],
  ["interest", "Interest"],
  ["insurance", "Insurance"],
  ["repair", "Repair"],
  ["misc", "Misc"],
  ["electricity", "Electricity"],
  ["fuel", "Fuel"],
  ["lubricants", "Lubricants"],
  ["crew", "Crew"],
];
const headLabelOf = (key: string) =>
  EQUIPMENT_HEAD_ORDER.find(([k]) => k === key)?.[1] || key;
const headOrderOf = (key: string) => {
  const index = EQUIPMENT_HEAD_ORDER.findIndex(([k]) => k === key);
  return index === -1 ? 99 : index;
};

type BreakdownKind = "materials" | "manpower" | "machinery";

interface BreakdownRow {
  key: string;
  level: 0 | 1 | 2 | 3;
  name: string;
  meta?: string;        // code / UOM
  countLabel?: string;  // "24 items" | "6 machines" | "3 tasks"
  plannedQty?: number;
  earnedQty?: number;
  planned: number;
  earned: number;
  /**
   * ₹ per unit of output, on the equipment cost-head and machine rows only.
   * Each divides by its OWN quantity — earned by earned, planned by planned —
   * so a pure volume difference never shows up as a rate variance. Absent when
   * that quantity is zero or the norm's matrix mixes units.
   */
  plannedPerUnit?: number;
  earnedPerUnit?: number;
  /** Output unit behind the two above, e.g. "Cum". */
  perUnitUom?: string;
  children?: BreakdownRow[];
}

// Type treatment per level — the ONLY hierarchy cue besides indentation.
const NAME_STYLE: Record<number, React.CSSProperties> = {
  0: { fontWeight: 600, color: "#022c57", fontSize: 13 },
  1: { fontWeight: 500, color: "#1f3c66" },
  2: { fontWeight: 500, color: "#526484" },
  3: { fontWeight: 400, color: "#64748b" },
};

const groupOf = (row: any, kind: BreakdownKind) =>
  kind === "manpower"
    ? row.source === "PRW"
      ? "PRW / Sub-Contract"
      : "Others (Task Resources)"
    : row.head === "TOOLS"
    ? "Tools & Machinery"
    : row.head === "EQUIPMENT"
    ? "Equipment Norms"
    : row.head || "Unassigned";

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

// Items → (equipment only) engine cost heads → machines, all as rows of one tree.
const buildBreakdownRows = (rows: any[], kind: BreakdownKind): BreakdownRow[] => {
  const itemRow = (row: any, index: number, prefix: string): BreakdownRow => {
    const isMaterial = kind === "materials";
    const name = isMaterial
      ? row.materialName || "Unlinked material"
      : row.itemName || "Unlinked item";
    const code = isMaterial ? row.materialCode : row.itemCode;
    const taskCount = Number(row.taskCount) || 0;
    const heads: BreakdownRow[] = (row.equipmentHeads || [])
      .slice()
      .sort((a: any, b: any) => headOrderOf(a.head) - headOrderOf(b.head))
      .map((head: any, hi: number) => {
        const assets: BreakdownRow[] = (head.assets || []).map(
          (asset: any, ai: number) => ({
            key: `${prefix}-${index}-h${hi}-a${ai}`,
            level: 3 as const,
            name: asset.assetName || "Unnamed machine",
            planned: num(asset.plannedAmount),
            earned: num(asset.earnedAmount),
            plannedPerUnit: optNum(asset.plannedPerUnit),
            earnedPerUnit: optNum(asset.earnedPerUnit),
            perUnitUom: row.uomName || "",
          })
        );
        return {
          key: `${prefix}-${index}-h${hi}`,
          level: 2 as const,
          name: headLabelOf(head.head),
          countLabel: assets.length ? plural(assets.length, "machine") : undefined,
          planned: num(head.plannedAmount),
          earned: num(head.earnedAmount),
          plannedPerUnit: optNum(head.plannedPerUnit),
          earnedPerUnit: optNum(head.earnedPerUnit),
          perUnitUom: row.uomName || "",
          children: assets.length ? assets : undefined,
        };
      });
    const plannedQty = num(row.monthQuantity);
    const earnedQty = num(row.earnedQuantity);
    const plannedAmount = num(row.monthCost);
    const earnedAmount = num(row.earnedCost);
    return {
      key: `${prefix}-${index}`,
      level: 1,
      name,
      meta: [code, row.uomName].filter(Boolean).join(" \u00b7 "),
      countLabel: taskCount ? plural(taskCount, "task") : undefined,
      plannedQty,
      earnedQty,
      planned: plannedAmount,
      earned: earnedAmount,
      // The item's own \u20b9/unit \u2014 the number this table's per-unit columns were
      // built for, but the one row that never filled them in. Each side uses
      // ITS OWN quantity: earning less volume at the same rate must not read
      // as a rate variance, which is exactly what dividing earned money by
      // PLANNED quantity would fabricate. undefined (not 0) when there is no
      // denominator, so perUnitCell renders an em dash.
      plannedPerUnit:
        plannedQty > 0 ? plannedAmount / plannedQty : undefined,
      earnedPerUnit: earnedQty > 0 ? earnedAmount / earnedQty : undefined,
      perUnitUom: row.uomName || "",
      children: heads.length ? heads : undefined,
    };
  };

  if (kind === "materials") return rows.map((row, index) => itemRow(row, index, "m"));

  const groups = new Map<string, any[]>();
  rows.forEach((row) => {
    const group = groupOf(row, kind);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(row);
  });
  return Array.from(groups.entries())
    .map(([group, items], gi) => ({
      key: `g${gi}`,
      level: 0 as const,
      name: group,
      countLabel: plural(items.length, "item"),
      planned: items.reduce((acc, row) => acc + num(row.monthCost), 0),
      earned: items.reduce((acc, row) => acc + num(row.earnedCost), 0),
      children: items.map((row, index) => itemRow(row, index, `g${gi}`)),
    }))
    .sort((a, b) => b.planned - a.planned);
};

const qtyCell = (value?: number) =>
  value === undefined ? null : (
    <span style={{ fontVariantNumeric: "tabular-nums", color: "#8094ae" }}>
      {fmtQty(value)}
    </span>
  );

/**
 * Optional numeric from the wire. Returns undefined for an ABSENT value rather
 * than 0 — the server omits a per-unit figure when it has no denominator, and
 * rendering that as "0.00" would read as "this head is free".
 */
const optNum = (value: any): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/** ₹ per unit of output. Blank on rows that have no such figure. */
const perUnitCell = (value?: number) =>
  value === undefined ? (
    <span style={{ color: "#c8cfd8" }}>—</span>
  ) : (
    <span style={{ fontVariantNumeric: "tabular-nums", color: "#526484" }}>
      {moneyPerUnit(value)}
    </span>
  );

const BreakdownTable: React.FC<{ rows: any[]; kind: BreakdownKind }> = ({
  rows,
  kind,
}) => {
  const data = React.useMemo(() => buildBreakdownRows(rows, kind), [rows, kind]);
  // Groups and their items open by default (cost heads visible); machines on
  // demand, so a 9-head × 6-machine norm doesn't flood the page.
  const defaultExpanded = React.useMemo(() => {
    const keys: string[] = [];
    data.forEach((group) => {
      keys.push(group.key);
      (group.children || []).forEach((item) => {
        if (item.children) keys.push(item.key);
      });
    });
    return keys;
  }, [data]);

  if (!rows.length) {
    return <Empty description="Nothing in this month" style={{ padding: "32px 0" }} />;
  }

  return (
    <Table
      size="small"
      key={`${kind}-${rows.length}`}
      rowKey="key"
      dataSource={data}
      scroll={{ x: 920 }}
      pagination={
        kind === "materials"
          ? { pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }
          : false
      }
      expandable={{ defaultExpandedRowKeys: defaultExpanded, indentSize: 18 }}
      columns={[
        {
          title: kind === "materials" ? "Material" : "Head / Item / Machine",
          dataIndex: "name",
          key: "name",
          ellipsis: true,
          render: (value: string, row: BreakdownRow) => (
            <span style={NAME_STYLE[row.level]}>
              {value}
              {row.meta ? (
                <span style={{ color: "#a9b6c9", fontWeight: 400, marginLeft: 8 }}>
                  {row.meta}
                </span>
              ) : null}
              {row.countLabel ? (
                <span style={{ color: "#a9b6c9", fontWeight: 400, marginLeft: 8 }}>
                  {row.countLabel}
                </span>
              ) : null}
            </span>
          ),
        },
        {
          title: "Planned Qty",
          dataIndex: "plannedQty",
          key: "pQty",
          width: 110,
          align: "right" as const,
          render: (value?: number) => qtyCell(value),
        },
        {
          title: "Planned Cost",
          dataIndex: "planned",
          key: "pCost",
          width: 125,
          align: "right" as const,
          render: (value: number) => costCell(value),
        },
        {
          // ₹ per unit of output, equipment cost-head and machine rows only.
          title: "Planned ₹/Unit",
          key: "pPerUnit",
          width: 120,
          align: "right" as const,
          render: (_: any, row: BreakdownRow) => perUnitCell(row.plannedPerUnit),
        },
        {
          title: "Target Qty",
          dataIndex: "earnedQty",
          key: "eQty",
          width: 110,
          align: "right" as const,
          render: (value?: number) => qtyCell(value),
        },
        {
          title: "Target Cost",
          dataIndex: "earned",
          key: "eCost",
          width: 125,
          align: "right" as const,
          render: (value: number) => earnedCell(value),
        },
        {
          // Divided by EXECUTED quantity, so this differs from Planned ₹/Unit
          // only when the rate moved — not when the volume did.
          title: "Target ₹/Unit",
          key: "ePerUnit",
          width: 120,
          align: "right" as const,
          render: (_: any, row: BreakdownRow) => perUnitCell(row.earnedPerUnit),
        },
        {
          title: "Deviation",
          key: "dev",
          width: 115,
          align: "right" as const,
          render: (_: any, row: BreakdownRow) =>
            deviationCell(row.earned - row.planned, row.earned),
        },
      ]}
    />
  );
};

// One activity's tasks for the month — fetched on expand, server-paginated.
const ActivityTasksTable: React.FC<{
  projectId: number | string;
  month: string;
  phaseId?: number;
  activityId: number;
}> = ({ projectId, month, phaseId, activityId }) => {
  const [rows, setRows] = useState<ExecutionTaskRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    threeMReportApis
      .getMonthlyExecutionMonthTasks(projectId, month, {
        phaseId,
        activityId,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          setRows(response?.data?.tasks || []);
          setCount(num(response?.data?.count));
        } else {
          message.error(response?.message || "Failed to load activity tasks");
        }
      })
      .catch(() => !cancelled && message.error("Failed to load activity tasks"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, activityId, page]);

  const columns: any[] = [
    { title: "Task ID", dataIndex: "taskSequenceId", key: "seq", width: 150, ellipsis: true },
    { title: "Task", dataIndex: "taskName", key: "name", width: 200, ellipsis: true },
    {
      title: "Sched Qty",
      key: "sched",
      width: 110,
      align: "right" as const,
      render: (_: any, row: ExecutionTaskRow) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#8094ae" }}>
          {fmtQty(row.scheduledQty)}
        </span>
      ),
    },
    {
      title: "Actual Qty",
      key: "actual",
      width: 110,
      align: "right" as const,
      render: (_: any, row: ExecutionTaskRow) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
          {fmtQty(row.actualQty)}{" "}
          <span style={{ color: "#8094ae", fontWeight: 400 }}>{row.workUom}</span>
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="Cumulative scheduled − cumulative actual through this month: the task's pending quantity">
          <span style={{ textDecoration: "underline dotted #b6c2d9", textUnderlineOffset: 3 }}>
            Carry Qty
          </span>
        </Tooltip>
      ),
      key: "carry",
      width: 110,
      align: "right" as const,
      render: (_: any, row: ExecutionTaskRow) => {
        const carry = num(row.carryQty);
        return (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              color: carry > 0 ? "#b46a12" : carry < 0 ? "#237804" : "#8c94a8",
            }}
          >
            {fmtQty(row.carryQty)}
          </span>
        );
      },
    },
    { title: "Planned Revenue", dataIndex: "targetAmount", key: "schedValue", width: 110, align: "right" as const, render: costCell },
    { title: "Actual Client Billing", dataIndex: "executedAmount", key: "actualValue", width: 120, align: "right" as const, render: costCell },
    { title: "Planned Exp.", dataIndex: "plannedOutflow", key: "planned", width: 110, align: "right" as const, render: costCell },
    { title: "Executed Exp. (Target)", dataIndex: "earnedOutflow", key: "earned", width: 125, align: "right" as const, render: earnedCell },
    {
      title: "Deviation (vs Planned)",
      key: "deviation",
      width: 125,
      align: "right" as const,
      render: (_: any, row: ExecutionTaskRow) =>
        deviationCell(row.outflowDeviation, row.earnedOutflow),
    },
    {
      title: "Remarks",
      dataIndex: "actualRemarks",
      key: "remarks",
      width: 160,
      ellipsis: true,
      render: (value: string) =>
        value ? <span style={{ fontSize: 12 }}>{value}</span> : null,
    },
  ];

  return (
    <Table
      size="small"
      rowKey="taskId"
      loading={loading}
      columns={columns}
      dataSource={rows}
      scroll={{ x: 1390 }}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: count,
        showSizeChanger: false,
        hideOnSinglePage: true,
        showTotal: (total) => `${total} tasks`,
        onChange: (nextPage) => setPage(nextPage),
      }}
    />
  );
};

const MonthlyExecutionMonthDetailPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const search = new URLSearchParams(location.search);

  const state = (location.state as any) || {};
  const projectId = state.id ?? params.projectId;
  const projectNameFromState = state.projectName;
  const wbsName = useWbsName();
  const month = search.get("month") || "";
  const initialTab = (search.get("tab") as ExecutionDetailTab) || "billing";
  const phaseId = state.phaseId as number | undefined;
  const baseActivityId = state.activityId as number | undefined;

  const [tab, setTab] = useState<ExecutionDetailTab>(initialTab);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);

  const [taskCount, setTaskCount] = useState(0);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [activityRollup, setActivityRollup] = useState<any[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (state.fullscreen && !document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* no gesture left — degrade to normal view */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Item tabs + month waterfall totals (one bounded fetch).
  useEffect(() => {
    if (!projectId || !month) return;
    let cancelled = false;
    setDetailLoading(true);
    threeMReportApis
      .getMonthlyExecutionMonthDetail(projectId, month, {
        phaseId,
        activityId: baseActivityId,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) setDetail(response?.data || {});
        else message.error(response?.message || "Failed to load month detail");
      })
      .catch(() => !cancelled && message.error("Failed to load month detail"))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, baseActivityId, refreshTick]);

  // Activity rollup + total count for the Tasks tab.
  useEffect(() => {
    if (!projectId || !month) return;
    let cancelled = false;
    setTasksLoading(true);
    threeMReportApis
      .getMonthlyExecutionMonthTasks(projectId, month, {
        phaseId,
        activityId: baseActivityId,
        page: 1,
        pageSize: 1,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          const payload = response?.data || {};
          setTaskCount(num(payload.count));
          setActivityRollup(payload.activityRollup || []);
        } else {
          message.error(response?.message || "Failed to load month tasks");
        }
      })
      .catch(() => !cancelled && message.error("Failed to load month tasks"))
      .finally(() => !cancelled && setTasksLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, baseActivityId, refreshTick]);

  const projectName = detail?.projectName || projectNameFromState || "Project";
  const monthTotals = detail?.monthTotals;

  const goBack = () =>
    navigate(`/pms/project/monthly-execution/${projectId}`, {
      state: { ...state, fullscreen: isFullscreen },
    });

  const activityColumns: any[] = [
    {
      title: "Activity",
      key: "activity",
      ellipsis: true,
      render: (_: any, row: any) => (
        <span style={{ fontWeight: 600, color: "#022c57" }}>
          {wbsName(row.activityName)}{" "}
          <span style={{ color: "#8094ae", fontWeight: 400 }}>({row.taskCount})</span>
        </span>
      ),
    },
    { title: "Planned Revenue", dataIndex: "targetAmount", key: "schedValue", width: 125, align: "right" as const, render: costCell },
    { title: "Actual Client Billing", dataIndex: "executedAmount", key: "actualValue", width: 135, align: "right" as const, render: costCell },
    { title: "Planned Exp.", dataIndex: "plannedOutflow", key: "planned", width: 125, align: "right" as const, render: costCell },
    { title: "Executed Exp. (Target)", dataIndex: "earnedOutflow", key: "earned", width: 140, align: "right" as const, render: earnedCell },
    {
      title: "Deviation (vs Planned)",
      key: "deviation",
      width: 140,
      align: "right" as const,
      render: (_: any, row: any) => deviationCell(row.outflowDeviation, row.earnedOutflow),
    },
  ];

  if (!projectId || !month) {
    return (
      <Content>
        <Empty description="Missing project or month context" style={{ padding: "80px 0" }} />
      </Content>
    );
  }

  return (
    <>
      <Head title="Monthly Execution — Month Detail" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "nowrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  style={{ paddingLeft: 0 }}
                  onClick={goBack}
                >
                  Monthly Execution
                </Button>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  {fmtMonthLong(month)} — Execution Detail
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Planned Revenue / Actual Client Billing = qty × Basic
                  Rate; Planned Expenditure = planned 3M cost; Executed Expenditure:
                  Target = at planned rates, Actual = consumed cost from daily site
                  captures
                </span>
              </div>
              {/* Page actions only — pinned to the top-right on the title's
                  line. The month's figures are NOT actions, so they get their
                  own left-aligned row below (see the stat row). */}
              <Space wrap style={{ marginLeft: "auto", flexShrink: 0, justifyContent: "flex-end" }}>
                <Button
                  icon={
                    isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={detailLoading || tasksLoading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* The month's figures, packed LEFT under the title. */}
            {monthTotals && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                {statChip("Planned Expenditure", num(monthTotals.targetAmount), "#022c57")}
                {statChip(
                  "Executed Exp. (Target)",
                  num(monthTotals.executedAmount),
                  "#1677ff"
                )}
                {monthTotals.hasActualCosts &&
                  statChip(
                    "Executed Exp. (Actual)",
                    num(monthTotals.actualOutflow),
                    "#17518D"
                  )}
                {num(monthTotals.actualClientBilling) > 0 &&
                  statChip(
                    "Actual Client Billing",
                    num(monthTotals.actualClientBilling),
                    "#0e5a8a"
                  )}
                {statChip(
                  "Month Variance",
                  num(monthTotals.monthVariance),
                  num(monthTotals.monthVariance) >= 0 ? "#237804" : "#cf1322",
                  true
                )}
                {statChip(
                  "Closing Backlog",
                  num(monthTotals.closingBacklog),
                  num(monthTotals.closingBacklog) > 0 ? "#b46a12" : "#237804",
                  true
                )}
              </div>
            )}

            {detailLoading && !detail ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <Tabs
                activeKey={tab}
                onChange={(key) => setTab(key as ExecutionDetailTab)}
                items={[
                  {
                    key: "billing",
                    label: "Billing",
                    children: (
                      <BillingDrillTab
                        projectId={projectId}
                        month={month}
                        phaseId={phaseId}
                        activityId={baseActivityId}
                      />
                    ),
                  },
                  {
                    key: "tasks",
                    label: `Tasks (${taskCount})`,
                    children: (
                      <Spin spinning={tasksLoading}>
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
                          Activity-wise breakdown, biggest target first — expand an
                          activity for its task-wise scheduled vs actual quantities,
                          carry-over and planned-vs-target 3M for this month.
                        </p>
                        <Table
                          size="small"
                          rowKey="activityId"
                          columns={activityColumns}
                          dataSource={activityRollup}
                          pagination={false}
                          scroll={{ x: 1080 }}
                          expandable={{
                            expandedRowRender: (activity: any) => (
                              <ActivityTasksTable
                                projectId={projectId}
                                month={month}
                                phaseId={phaseId}
                                activityId={activity.activityId}
                              />
                            ),
                          }}
                        />
                      </Spin>
                    ),
                  },
                  {
                    key: "materials",
                    label: `Materials (${detail?.materials?.length || 0})`,
                    children: (
                      <>
                        <BreakdownTable rows={detail?.materials || []} kind="materials" />
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                          Planned = norm/BOQ quantities × schedule share; Target = the
                          same per-unit basis × actual share (executed at planned
                          rates). Deviation (vs Planned) follows the quantity
                          shortfall — cost-efficiency vs Actual lives on the top
                          sheet's Deviation sub-column.
                        </p>
                      </>
                    ),
                  },
                  {
                    key: "manpower",
                    label: `Manpower (${detail?.manpower?.length || 0})`,
                    children: <BreakdownTable rows={detail?.manpower || []} kind="manpower" />,
                  },
                  {
                    key: "machinery",
                    label: `Machinery (${detail?.machinery?.length || 0})`,
                    children: (
                      <>
                        <BreakdownTable rows={detail?.machinery || []} kind="machinery" />
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                          Tools & Machinery rows include their consumables' cost share;
                          Equipment Norms are live-derived from deployment norms.
                        </p>
                      </>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </Card>
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default MonthlyExecutionMonthDetailPage;
